import {
  Accordion,
  Box,
  Button,
  Card,
  Checkbox,
  createStyles,
  Flex,
  Grid,
  Image,
  Space,
  Stack,
  Text,
  useMantineTheme,
} from '@mantine/core';
import React, { useEffect } from 'react';
import { FiArrowRight, FiFile } from 'react-icons/fi';

import ButtonLink from '@unserkunde/enscompare-components/src/components/button/ButtonLink';
import Checklist from '@unserkunde/enscompare-components/src/components/feedback/Checklist';
import Eyebrow, {
  EyebrowProps,
} from '@unserkunde/enscompare-components/src/components/feedback/Eyebrow';
import TestSeal from '@unserkunde/enscompare-components/src/components/feedback/TestSeal';
import { useIsXs } from '@unserkunde/enscompare-components/src/components/misc/hooks';
import { Price } from '../components';

const useStyles = createStyles((theme, props) => ({
  card: {
    padding: theme.spacing.xs,
    [theme.fn.largerThan('sm')]: {
      padding: theme.spacing.md,
    },
    [theme.fn.largerThan('sm')]: {
      padding: theme.spacing.md,
    },
    [theme.fn.largerThan('lg')]: {
      padding: theme.spacing.lg,
    },
    overflow: 'visible',
  },
  flexMaxContent: {
    flexBasis: 'max-content',
  },
  flexStretch: {
    justifyContent: 'space-between',
  },
  contentClass: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  actionGrid: {
    flexGrow: 1,
  },
}));

const DocumentUrl = ({ document }) => {
  return (
    <ButtonLink {...document} leftIcon={<FiFile />} key={document.uuid} wrap />
  );
};

const InsuranceCard = ({
  icon = undefined as React.ComponentProps<typeof Image>,
  tariffTitle = undefined as React.ReactNode,
  tariffDescr = undefined as React.ReactNode,
  testSeal = undefined as
    | React.ComponentProps<typeof TestSeal>
    | React.ComponentProps<typeof Image>,
  price = null,
  btnLabel = undefined as React.ReactNode,
  btnCallback = () => {},
  documents = [] as React.ComponentProps<typeof ButtonLink>[],
  checklist = [] as React.ComponentProps<typeof Checklist>,
  disclaimer = undefined as React.ReactNode,
  checkbox = undefined as React.ComponentProps<typeof Checkbox>,
  priceComponent = Price,
  disabled = false,
  eyebrow = undefined as EyebrowProps,
  useSmall = false,
  ...rest
}) => {
  const theme = useMantineTheme();

  const isSmall = useSmall;

  const { classes } = useStyles();

  const { contentClassName, className, ...additionalProps } = rest;

  const [docsOpen, setDocsOpen] = React.useState(null);

  const isXs = useIsXs() || isSmall;

  useEffect(() => {
    if (!isXs) return;
    setDocsOpen(null);
  }, [isXs]);

  const maxDocs = React.useMemo(() => (isXs ? 0 : 2), [isXs]);

  const PriceComp = priceComponent;

  return (
    <Card
      {...additionalProps}
      {...(!disabled ? null : { sx: { background: theme.colors.gray[1] } })}
      className={`${classes.card} ${className}`}
    >
      {!eyebrow ? null : <Eyebrow {...eyebrow} text={eyebrow?.children} />}
      <Box
        px={0}
        className={`${contentClassName || ''} ${classes.contentClass}`}
      >
        <Flex direction="row" gap="sm" className={classes.flexStretch} mb="xs">
          <div className={classes.flexMaxContent}>
            <Checkbox {...checkbox} {...(disabled ? { disabled } : null)} />
          </div>
          <PriceComp {...price} size="md" />
        </Flex>

        <Grid
          columns={3}
          justify="space-between"
          className={classes.actionGrid}
        >
          <Grid.Col span={1}>
            <Flex direction="column" gap="md" align="flex-start">
              {React.isValidElement(icon) ? (
                icon
              ) : (
                <Image mb="xs" maw={54} {...icon} />
              )}

              {documents && (
                <Box>
                  {maxDocs > 0 && (
                    <Stack align="flex-start" spacing="xs">
                      {documents?.slice(0, maxDocs).map((doc) => (
                        <DocumentUrl document={doc} />
                      ))}
                    </Stack>
                  )}

                  {documents?.length <= maxDocs ? null : (
                    <>
                      <Space h="xs" />

                      <Accordion
                        unstyled
                        value={docsOpen}
                        onChange={setDocsOpen}
                      >
                        <Accordion.Item value="seeMore">
                          <Accordion.Panel>
                            <Stack align="flex-start" spacing="xs">
                              {documents?.slice(maxDocs).map((doc) => (
                                <DocumentUrl document={doc} />
                              ))}
                            </Stack>
                          </Accordion.Panel>
                          <Accordion.Control
                            sx={{
                              display: 'flex',
                              flexDirection: 'row-reverse',
                              backgroundColor: 'transparent',
                              border: 'none',
                              gap: 6,
                              fontSize: 16,
                              padding: '8px 0',
                              '& *[class*="-Accordion-chevron"][data-rotate]': {
                                transform: 'rotate(180deg)',
                              },
                            }}
                          >
                            {docsOpen === null
                              ? `${isXs ? 'Dokumente' : 'Alle'} ansehen`
                              : 'Verbergen'}
                          </Accordion.Control>
                        </Accordion.Item>
                      </Accordion>
                    </>
                  )}
                </Box>
              )}
            </Flex>
          </Grid.Col>

          <Grid.Col span={2} sm={isSmall ? 2 : 1}>
            <Text weight={700}>{tariffTitle}</Text>
            <Text mb="sm">{tariffDescr}</Text>
            <Checklist preview={6} {...checklist} />
          </Grid.Col>

          <Grid.Col
            span={3}
            sm={isSmall ? 3 : 1}
            display="flex"
            style={{ flexDirection: 'column', justifyContent: 'flex-end' }}
          >
            <div style={{ flexGrow: 1 }} />
            {
              // eslint-disable-next-line no-nested-ternary
              // @ts-ignore
              !testSeal ? null : !testSeal?.image ? (
                // @ts-ignore
                <TestSeal {...testSeal} size="md" />
              ) : (
                // @ts-ignoreF
                <Image {...testSeal} />
              )
            }

            <Text c="dimmed" mb="md">
              {disclaimer}
            </Text>

            <Button
              style={{ minHeight: 'max-content' }}
              fullWidth
              color="primary"
              rightIcon={<FiArrowRight />}
              onClick={btnCallback}
              {...(disabled ? { disabled } : null)}
            >
              {btnLabel}
            </Button>
          </Grid.Col>
        </Grid>
      </Box>
    </Card>
  );
};

export default InsuranceCard;
